import { supabase } from '../lib/supabase';

export interface City {
  id: string;
  name: string;
  name_ar: string;
  name_fr: string;
  region: string;
  region_ar: string;
  region_fr: string;
  country: string;
  latitude: number;
  longitude: number;
  population?: number;
  is_popular: boolean;
  is_active: boolean;
  image_url?: string;
  created_at: string;
  updated_at: string;
  city_search_terms?: Array<{
    id: string;
    search_term: string;
    language: string;
  }>;
}

/**
 * Search cities by name or search terms
 */
export const searchCities = async (
  query: string, 
  country: string = 'Morocco', 
  limit: number = 10
): Promise<City[]> => {
  if (!query || query.length < 1) return [];

  const searchQuery = query.toLowerCase().trim();

  try {
    // Search in cities table and related search terms
    const { data, error } = await supabase
      .from('cities')
      .select(`
        *,
        city_search_terms(
          id,
          search_term,
          language
        )
      `)
      .eq('country', country)
      .eq('is_active', true)
      .or(`name.ilike.%${searchQuery}%,name_ar.ilike.%${searchQuery}%,name_fr.ilike.%${searchQuery}%`)
      .order('is_popular', { ascending: false })
      .order('population', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error searching cities:', error);
      return [];
    }

    // Also search in search terms table
    const { data: termResults, error: termError } = await supabase
      .from('city_search_terms')
      .select(`
        search_term,
        cities!inner(
          *,
          city_search_terms(
            id,
            search_term,
            language
          )
        )
      `)
      .ilike('search_term', `%${searchQuery}%`)
      .eq('cities.country', country)
      .eq('cities.is_active', true)
      .limit(limit);

    if (termError) {
      console.error('Error searching city terms:', termError);
    }

    // Combine results and remove duplicates
    const allResults = [...(data || [])];
    
    if (termResults) {
      termResults.forEach((termResult: any) => {
        const city = termResult.cities as unknown as City;
        if (city && !allResults.find(r => r.id === city.id)) {
          allResults.push(city);
        }
      });
    }

    // Sort by relevance (popular cities first, then by population)
    return allResults
      .sort((a, b) => {
        if (a.is_popular && !b.is_popular) return -1;
        if (!a.is_popular && b.is_popular) return 1;
        return (b.population || 0) - (a.population || 0);
      })
      .slice(0, limit);

  } catch (error) {
    console.error('Error in searchCities:', error);
    return [];
  }
};

/**
 * Get popular cities
 */
export const getPopularCities = async (
  country: string = 'Morocco',
  limit: number = 10
): Promise<City[]> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .select(`
        *,
        city_search_terms(
          id,
          search_term,
          language
        )
      `)
      .eq('country', country)
      .eq('is_popular', true)
      .eq('is_active', true)
      .order('population', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching popular cities:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getPopularCities:', error);
    return [];
  }
};

/**
 * Get city by ID
 */
export const getCityById = async (id: string): Promise<City | null> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .select(`
        *,
        city_search_terms(
          id,
          search_term,
          language
        )
      `)
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error fetching city by ID:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getCityById:', error);
    return null;
  }
};

/**
 * Get city by name
 */
export const getCityByName = async (
  name: string, 
  country: string = 'Morocco'
): Promise<City | null> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .select(`
        *,
        city_search_terms(
          id,
          search_term,
          language
        )
      `)
      .eq('country', country)
      .eq('is_active', true)
      .or(`name.ilike.${name},name_ar.ilike.${name},name_fr.ilike.${name}`)
      .single();

    if (error) {
      console.error('Error fetching city by name:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getCityByName:', error);
    return null;
  }
};

/**
 * Get all cities for a country
 */
export const getAllCities = async (
  country: string = 'Morocco'
): Promise<City[]> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .select(`
        *,
        city_search_terms(
          id,
          search_term,
          language
        )
      `)
      .eq('country', country)
      .eq('is_active', true)
      .order('is_popular', { ascending: false })
      .order('population', { ascending: false });

    if (error) {
      console.error('Error fetching all cities:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllCities:', error);
    return [];
  }
};

/**
 * Admin functions (for future use)
 */

/**
 * Add a new city (admin only)
 */
export const addCity = async (cityData: Omit<City, 'id' | 'created_at' | 'updated_at'>): Promise<City | null> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .insert([cityData])
      .select()
      .single();

    if (error) {
      console.error('Error adding city:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in addCity:', error);
    return null;
  }
};

/**
 * Update city (admin only)
 */
export const updateCity = async (id: string, updates: Partial<City>): Promise<City | null> => {
  try {
    const { data, error } = await supabase
      .from('cities')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating city:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in updateCity:', error);
    return null;
  }
};

/**
 * Delete city (admin only)
 */
export const deleteCity = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('cities')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting city:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteCity:', error);
    return false;
  }
};
